/* eslint-disable react/prop-types */
import { InfoIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import CreatableSelect from "react-select/creatable";

const operatorOptions = [
  { value: "==", label: "is equal to" },
  { value: "!=", label: "is not equal to" },
  { value: ">", label: "is greater than" },
  { value: "<", label: "is less than" },
  { value: ">=", label: "is greater or equal" },
  { value: "<=", label: "is less or equal" },
  { value: "in", label: "is in" },
  { value: "contains", label: "contains" },
];

const orderOptions = [
  { value: "all", label: "All" },
  { value: "any", label: "Any" },
];

export default function EditConditionsForm({ data = {}, onSubmit }) {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      Field: data.Field || "",
      Operator: data.Operator
        ? operatorOptions.find((op) => op.value === data.Operator)
        : null,
      Value: (() => {
        const val = data.Value;
        if (Array.isArray(val)) {
          return val.map((v) => ({ value: v, label: v }));
        } else if (typeof val === "string") {
          return [{ value: val, label: val }];
        } else {
          return [];
        }
      })(),
      Reference: data.Reference || "",
      Order: data.Order
        ? orderOptions.find((o) => o.value === data.Order)
        : null,
    },
  });

  return (
    <Box
      as="form"
      onSubmit={handleSubmit((values) => {
        // Convert back: single value -> string, multiple -> array
        let finalValue;
        if (values.Value.length === 0) {
          finalValue = "";
        } else if (values.Value.length === 1) {
          finalValue = values.Value[0].value;
        } else {
          finalValue = values.Value.map((v) => v.value);
        }

        const formattedValues = {
          ...values,
          Value: finalValue,
          Operator: values.Operator?.value || "",
          Order: values.Order?.value || "",
        };

        onSubmit(formattedValues);
      })}
      p={1}
      boxShadow="sm"
      maxW="600px"
    >
      <VStack spacing={5} align="stretch">
        {/* FIELD */}
        <FormControl>
          <FormLabel fontWeight="medium">Field</FormLabel>
          <Controller
            name="Field"
            control={control}
            render={({ field }) => (
              <Input
                borderRadius="md"
                borderColor="gray.300"
                bg="white"
                {...field}
              />
            )}
          />
        </FormControl>

        {/* OPERATOR + ORDER */}
        <HStack spacing={5}>
          <FormControl flex="1">
            <FormLabel fontWeight="medium">Operator</FormLabel>
            <Controller
              name="Operator"
              control={control}
              render={({ field }) => (
                <CreatableSelect
                  placeholder="Select operator"
                  options={operatorOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FormControl>

          <FormControl flex="1">
            <FormLabel fontWeight="medium">Order</FormLabel>
            <Controller
              name="Order"
              control={control}
              render={({ field }) => (
                <CreatableSelect
                  placeholder="Select order"
                  options={orderOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FormControl>
        </HStack>

        <HStack spacing={5}>
          {/* VALUE */}
          <FormControl>
            <Flex align="center" gap={1}>
              <FormLabel fontWeight="medium" mb={0}>
                Value
              </FormLabel>
              <Tooltip label="You can add multiple value by create option">
                <InfoIcon />
              </Tooltip>
            </Flex>

            <Controller
              name="Value"
              control={control}
              render={({ field }) => (
                <CreatableSelect
                  isMulti
                  placeholder="Select or type values"
                  value={field.value} // already array of objects
                  onChange={(val) => field.onChange(val || [])} // always array
                />
              )}
            />
          </FormControl>

          {/* REFERENCE */}
          <FormControl>
            <FormLabel fontWeight="medium">Reference</FormLabel>
            <Controller
              name="Reference"
              control={control}
              render={({ field }) => (
                <Input
                  borderRadius="md"
                  borderColor="gray.300"
                  bg="white"
                  {...field}
                />
              )}
            />
          </FormControl>
        </HStack>

        {/* SUBMIT */}
        <Button type="submit" variant="primaryButton" w="full">
          Update This Condition
        </Button>
      </VStack>
    </Box>
  );
}
